import { A } from '@solidjs/router';
import { createEffect, createSignal, For, onCleanup, Show } from 'solid-js';
import PublicLayout from '../PublicLayout';

const POSTS = [
  {
    slug: 'crystal-ball',
    title:
      'The Warehouse Crystal Ball: Using AI to Predict Demand Before It Happens',
    description:
      'Tired of running out of bestsellers while sitting on slow-moving stock? Learn how AI-powered demand forecasting calculates sales velocity per SKU, surfaces confidence-scored predictions, and turns forecasting intelligence into one-click purchase orders.',
    date: 'May 9, 2026',
    readTime: '9 min read',
    category: 'AI & Forecasting',
    image: '/blog-forecast-hero.jpg',
  },
  {
    slug: 'growth-trap',
    title:
      'The Growth Trap: Why Your Manual Systems Will Break at 50 Orders a Day',
    description:
      'What worked at 5 orders a day becomes a nightmare at 50. Learn about "technical debt" in the warehouse, why barcode scanning is the single biggest accuracy upgrade, and the 60-second stress test to know if you\'re ready to scale.',
    date: 'May 8, 2026',
    readTime: '8 min read',
    category: 'Operations Strategy',
    image: '/blog-scaling-chaos.jpg',
  },
  {
    slug: 'ghost-inventory',
    title:
      'The Ghost in the Machine: Eliminating Phantom Stock and Dead Inventory',
    description:
      'Phantom stock and dead inventory are silent profit killers. Learn how cycle counting, lean reorder points, FIFO batch tracking, and real-time sync eliminate them for good.',
    date: 'May 6, 2026',
    readTime: '9 min read',
    category: 'Inventory Control',
    image: '/blog-ghost-inventory.jpg',
  },
  {
    slug: 'walking-tax',
    title:
      'The Walking Tax: How Warehouse Layout Is Quietly Draining Your Labor Budget',
    description:
      'In an unoptimized warehouse, a single worker can walk 10+ miles per shift. Learn how velocity slotting and zone picking cut floor travel by 20% — the equivalent of a free staff member.',
    date: 'May 6, 2026',
    readTime: '8 min read',
    category: 'Labor Efficiency',
    image: '/blog-walking-tax-tired-worker.jpg',
  },
  {
    slug: 'spreadsheet-trap',
    title:
      'The Spreadsheet Trap: What Your Inventory System Is Costing You Right Now',
    description:
      'Ghost stock, data fragmentation, and zero real-time visibility — the hidden costs of running warehouse operations on spreadsheets, and how modern platforms eliminate them.',
    date: 'May 6, 2026',
    readTime: '7 min read',
    category: 'Operations',
    image: '/blog-spreadsheet-trap-hero.jpg',
  },
];

const PAGE_SIZE = 3;

export default function BlogsIndexPage() {
  createEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Khoai Blog',
      description:
        'Insights on inventory management, warehouse operations, and logistics automation from the Khoai team.',
      url: 'https://khoai.io/blogs',
      publisher: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
      },
    };

    let script = document.getElementById(
      'ld-json-blogs-index'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-blogs-index';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    let listScript = document.getElementById(
      'ld-json-blogs-index-list'
    ) as HTMLScriptElement | null;
    if (!listScript) {
      listScript = document.createElement('script');
      listScript.type = 'application/ld+json';
      listScript.id = 'ld-json-blogs-index-list';
      document.head.appendChild(listScript);
    }
    listScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Khoai Blog Posts',
      url: 'https://khoai.io/blogs',
      itemListElement: POSTS.map((post, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: post.title,
        url: `https://khoai.io/blogs/${post.slug}`,
      })),
    });
  });

  onCleanup(() => {
    document.getElementById('ld-json-blogs-index')?.remove();
    document.getElementById('ld-json-blogs-index-list')?.remove();
  });

  const [page, setPage] = createSignal(0);
  const totalPages = () => Math.ceil(POSTS.length / PAGE_SIZE);
  const pagePosts = () =>
    POSTS.slice(page() * PAGE_SIZE, page() * PAGE_SIZE + PAGE_SIZE);

  return (
    <PublicLayout>
      <div class="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Page header */}
        <header class="mb-12 text-center">
          <p class="text-sm font-semibold uppercase tracking-widest text-amber-600">
            Khoai Blog
          </p>
          <h1 class="mt-2 text-4xl font-extrabold text-gray-900 sm:text-5xl">
            Insights for Warehouse Operators
          </h1>
          <p class="mx-auto mt-4 max-w-2xl text-lg text-gray-500">
            Practical writing on inventory management, logistics automation, and
            building operations that scale — from the team behind Khoai.
          </p>
        </header>

        {/* Post list */}
        <div class="space-y-10">
          <For each={pagePosts()}>
            {(post) => (
              <article class="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md sm:flex-row">
                {/* Thumbnail */}
                <div class="shrink-0 sm:w-64">
                  <img
                    src={post.image}
                    alt={post.title}
                    class="h-52 w-full object-cover sm:h-full"
                    loading="lazy"
                  />
                </div>

                {/* Content */}
                <div class="flex flex-col justify-between p-6">
                  <div>
                    <div class="flex items-center gap-3">
                      <span class="rounded-full bg-amber-100 px-3 py-0.5 text-xs font-semibold text-amber-700">
                        {post.category}
                      </span>
                      <span class="text-xs text-gray-400">{post.readTime}</span>
                    </div>
                    <h2 class="mt-3 text-xl font-bold text-gray-900 group-hover:text-amber-700 sm:text-2xl">
                      <A href={`/blogs/${post.slug}`}>{post.title}</A>
                    </h2>
                    <p class="mt-2 text-sm leading-relaxed text-gray-500">
                      {post.description}
                    </p>
                  </div>
                  <div class="mt-5 flex items-center justify-between">
                    <span class="text-xs text-gray-400">{post.date}</span>
                    <A
                      href={`/blogs/${post.slug}`}
                      class="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
                    >
                      Read article →
                    </A>
                  </div>
                </div>
              </article>
            )}
          </For>
        </div>

        {/* Pagination */}
        <Show when={totalPages() > 1}>
          <nav
            class="mt-10 flex items-center justify-center gap-4"
            aria-label="Blog pagination"
          >
            <button
              disabled={page() === 0}
              onClick={() => {
                setPage((p) => p - 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              ← Previous
            </button>
            <span class="text-sm text-gray-500">
              Page {page() + 1} of {totalPages()}
            </span>
            <button
              disabled={page() === totalPages() - 1}
              onClick={() => {
                setPage((p) => p + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next →
            </button>
          </nav>
        </Show>
      </div>
    </PublicLayout>
  );
}
